import { Stack, Typography, useTheme } from '@mui/material';
import Chart from 'react-apexcharts';
import { useGatewayReadings } from 'src/features/sites/hooks';
import { chartSeries, getChartOptions } from '../../config';
import { NoDataAvailableBox } from '../NoDataAvailableBox';
import { cardChartLabel } from '../style';
import { WidgetWrapper } from '../WidgetWrapper';
import { UserTypes } from 'src/features/authentication/types';

type Props = {
  type: 'continuous' | 'discrete';
  title: string;
  unit: string;
  isEditMode?: boolean;
  widgetId?: string;
  values: any[] | null;
  toggleAddOrRemoveHandler?: () => void;
  testId?: string;
  gatewayInput: any;
  customerId?: string;
  user?: UserTypes;
};

export const TrendWidget = ({
  type,
  title,
  unit,
  isEditMode,
  widgetId,
  values,
  toggleAddOrRemoveHandler,
  testId,
  gatewayInput,
}: Props) => {
  const theme = useTheme();
  const gatewayId = gatewayInput?.gatewayId;
  const tagId = gatewayInput?.id;
  const { readings, isDataReady, display } = useGatewayReadings({
    gatewayId: gatewayId || '',
    tagId: tagId || '',
  });

  const rawInputNameReadings = readings && readings[tagId];
  const readingsValues = rawInputNameReadings && rawInputNameReadings.values;
  const newData = readingsValues && Object.entries(readingsValues);

  const formatNewData = newData?.flatMap((reading: any) => {
    const formattedTimestamp = reading[0] && new Date(reading[0]);
    const localTime = new Date(
      formattedTimestamp.getTime() - formattedTimestamp.getTimezoneOffset() * 60 * 1000
    );
    return {
      timestamp: new Date(localTime).toString(),
      input: +reading[1] && reading[1],
      inputName: rawInputNameReadings && rawInputNameReadings.displayName,
      rawUnit: rawInputNameReadings && rawInputNameReadings.rawUnit,
    };
  });

  const combinedValues = [...(formatNewData || []), ...(values || [])];

  const sortedCombinedValues = combinedValues.sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  const data = sortedCombinedValues.length ? sortedCombinedValues : [];

  const uniqueDataValues =
    type === 'discrete'
      ? [...new Set(data.map((d) => Number(d.input)).filter((v) => !isNaN(v)))]
      : undefined;
  const options = getChartOptions(type, display, uniqueDataValues);
  const series = isDataReady ? chartSeries(data) : [];

  const noFormatedData = formatNewData?.length === 0 || !formatNewData;
  const noDataAvailable = !values && noFormatedData;

  const dataTestId = series[0]?.data?.length ? 'data-available' : 'no-data';

  return (
    <WidgetWrapper
      title={title}
      isEditMode={isEditMode}
      widgetId={widgetId!}
      toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
      testId={testId}
      isDataReady={isDataReady}
      isTrendWidget={true}
      options={options}
      series={series}
      typeChart="area"
      unit={unit}
      noDataAvailable={noDataAvailable}
    >
      <Stack sx={cardChartLabel} data-sm={dataTestId}>
        <Typography variant="caption" sx={{ color: theme.palette.grey[500] }}>
          {unit}
        </Typography>
      </Stack>
      {noDataAvailable ? (
        <NoDataAvailableBox />
      ) : (
        <Stack sx={{ flex: 1, minHeight: '200px' }}>
          <Chart options={options} series={series} type="area" width="100%" height="100%" />
        </Stack>
      )}
    </WidgetWrapper>
  );
};
